import { useState } from "react";
import { Link } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceToTextTextarea } from "@/components/voice-field";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Users, Plus, Loader2, ArrowRight, DollarSign } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { PlatformCustomer, Workspace } from "@shared/schema";

type CustomersListResponse = {
  customers: PlatformCustomer[];
  summary: { mrrCents: number; counts: Record<string, number>; total: number };
};

function formatMoneyCents(cents: number) {
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function statusBadgeClass(status: string) {
  switch (status) {
    case "active": return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
    case "trial": return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
    case "past_due": return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
    case "canceled":
    case "churned": return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
    default: return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200";
  }
}

export default function CustomersPage() {
  const { toast } = useToast();
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [createOpen, setCreateOpen] = useState(false);

  const queryKey = ["/api/platform-customers", statusFilter] as const;
  const { data, isLoading, isError } = useQuery<CustomersListResponse>({
    queryKey: statusFilter === "all"
      ? ["/api/platform-customers"]
      : ["/api/platform-customers", { status: statusFilter }],
    retry: false,
  });

  // Create form state
  const [form, setForm] = useState({
    workspaceSlug: "",
    workspaceName: "",
    companyName: "",
    planTier: "lite",
    notes: "",
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/platform-customers", form);
      return res.json();
    },
    onSuccess: (data: any) => {
      toast({ title: "Customer created", description: `Workspace ${data.workspace?.slug} provisioned` });
      setCreateOpen(false);
      setForm({ workspaceSlug: "", workspaceName: "", companyName: "", planTier: "lite", notes: "" });
      queryClient.invalidateQueries({ queryKey: ["/api/platform-customers"] });
    },
    onError: (e: any) => {
      toast({ variant: "destructive", title: "Create failed", description: e?.message || "Could not create customer" });
    },
  });

  const customers = data?.customers || [];
  const summary = data?.summary;

  return (
    <div className="p-6 space-y-6" data-testid="page-customers">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Users className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-2xl font-semibold">Customers</h1>
            <p className="text-sm text-muted-foreground">SaaS accounts subscribed to Clarik</p>
          </div>
        </div>

        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="button-new-customer"><Plus className="h-4 w-4 mr-2" />New Customer</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Provision a new customer</DialogTitle>
              <DialogDescription>
                Creates a new tenant workspace and a platform customer record linking it.
                Stripe billing is wired up separately once the customer goes through checkout.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="cust-company">Company name</Label>
                <Input id="cust-company" data-testid="input-company-name" value={form.companyName}
                  onChange={(e) => setForm({ ...form, companyName: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="cust-slug">Workspace slug</Label>
                  <Input id="cust-slug" data-testid="input-workspace-slug" placeholder="acme-music"
                    value={form.workspaceSlug}
                    onChange={(e) => setForm({ ...form, workspaceSlug: e.target.value.toLowerCase() })} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="cust-name">Workspace name</Label>
                  <Input id="cust-name" data-testid="input-workspace-name" placeholder="Acme Music"
                    value={form.workspaceName}
                    onChange={(e) => setForm({ ...form, workspaceName: e.target.value })} />
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="cust-tier">Plan</Label>
                <Select value={form.planTier} onValueChange={(v) => setForm({ ...form, planTier: v })}>
                  <SelectTrigger id="cust-tier" data-testid="select-plan-tier"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lite">Lite</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="business">Business</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="cust-notes">Notes</Label>
                <VoiceToTextTextarea id="cust-notes" data-testid="input-notes" rows={3}
                  value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button
                data-testid="button-submit-customer"
                disabled={createMutation.isPending || !form.companyName || !form.workspaceSlug || !form.workspaceName}
                onClick={() => createMutation.mutate()}>
                {createMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2"><CardDescription>Monthly recurring revenue</CardDescription></CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold flex items-center gap-2" data-testid="text-mrr">
              <DollarSign className="h-5 w-5 text-muted-foreground" />
              {summary ? formatMoneyCents(summary.mrrCents) : "—"}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardDescription>Active customers</CardDescription></CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold" data-testid="text-active-count">
              {summary?.counts.active ?? 0}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardDescription>Total accounts</CardDescription></CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold" data-testid="text-total-count">
              {summary?.total ?? 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-3">
        <Label className="text-sm">Status</Label>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="select-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="trial">Trial</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="past_due">Past Due</SelectItem>
            <SelectItem value="canceled">Canceled</SelectItem>
            <SelectItem value="churned">Churned</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardHeader><CardTitle>Customer list</CardTitle></CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-8 flex items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 mr-2 animate-spin" /> Loading...
            </div>
          ) : isError ? (
            <div className="py-8 text-center text-destructive" data-testid="state-customers-error">
              Failed to load customers
            </div>
          ) : customers.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground" data-testid="state-customers-empty">
              No customers yet. Click "New Customer" to provision your first one.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>MRR</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customers.map((c) => (
                  <TableRow key={c.id} data-testid={`row-customer-${c.id}`}>
                    <TableCell className="font-medium" data-testid={`text-company-${c.id}`}>{c.companyName}</TableCell>
                    <TableCell><Badge variant="outline">{c.planTier || "—"}</Badge></TableCell>
                    <TableCell>
                      <Badge className={statusBadgeClass(c.status)} data-testid={`badge-status-${c.id}`}>
                        {c.status}
                      </Badge>
                    </TableCell>
                    <TableCell data-testid={`text-mrr-${c.id}`}>{formatMoneyCents(c.mrrCents || 0)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(c.createdAt as any).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Link href={`/customers/${c.id}`}>
                        <Button variant="ghost" size="sm" data-testid={`link-detail-${c.id}`}>
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
